import React, { Component } from 'react';
import { withRouterV6 as withRouter } from '../../../helpers/withRouterV6.jsx';
import { I18n } from 'react-redux-i18n';

import get from 'lodash.get';
import { Edit3, Eye, EyeOff, DollarSign, History, Send, Trash2, ExternalLink, RotateCcw } from 'lucide-react';

import {
  donationI18Keys,
  donationSettings,
  donationStatusBackgroundColor
} from './constants/donationSettings';
import { StatusBadgeGroup } from '../../../components/common/StatusBadge';

import currencyHelper from '../../../helpers/currencyHelper';
import { EntityHeaderActions } from '../../../components/common/EntityHeaderActions/EntityHeaderActions';
import CurrencyDisplay from '../../../components/common/CurrencyDisplay';
import donationType from '../../../constants/donationType';
import config from '../../../constants';
import { isProduction } from '../../../utils/environmentUtils';
import { stripeName } from '../../organization/constants';

import { CONTEXT_MENU_KEYS } from '../../../constants/contextMenuEntries.jsx';

class DonationHeader extends Component {
  getStripeUrl() {
    const { donation, customPaymentPlatform, isPaymentPlatformsLoaded, isSystemAdmin } = this.props;
    const financials = get(donation, 'financials');
    const paymentPlatforms = get(this.props, 'paymentPlatforms', []);
    const alternativePaymentPlatforms = get(this.props, 'alternativePaymentPlatforms', []);

    const chargeKey = get(financials, 'platformDetails.chargeKey');
    const intentKey = get(financials, 'platformDetails.intentKey');
    const paymentKey = intentKey || chargeKey;

    if (!paymentKey) return null;

    // Get account key from payment platforms
    let accountKey = null;
    if (customPaymentPlatform) {
      accountKey = customPaymentPlatform.accountKey;
    } else {
      const platform =
        alternativePaymentPlatforms?.find(
          (p) => p.platform === stripeName && p.purpose?.toLowerCase() === 'sale'
        ) || paymentPlatforms?.find((p) => p.platform === stripeName);
      accountKey = platform?.accountIdentifier || platform?.accountKey;
    }

    const sysadminAccountId = config.STRIPE_SYSADMIN_ACCOUNT_ID;
    const isProductionEnv = isProduction();
    const accountKeyContext = isProductionEnv ? accountKey : `${accountKey}/test`;
    const sysadminBaseUrl = sysadminAccountId
      ? `https://dashboard.stripe.com/${sysadminAccountId}/connect/view-as/${accountKeyContext}`
      : `https://dashboard.stripe.com/${accountKeyContext}`;

    if (isSystemAdmin && accountKey && isPaymentPlatformsLoaded) {
      return `${sysadminBaseUrl}/payments/${paymentKey}`;
    } else if (!isSystemAdmin && paymentKey) {
      return `https://dashboard.stripe.com/payments/${paymentKey}`;
    }

    return null;
  }

  render() {
    const { financials, type, amount: donationAmount, currency: donationCurrency, organization, id: donationId, donor, saleId, subscriptionId, receiptIdentifier } = this.props.donation;
    const { isShowEmptyFields, displayOrgCurrency, relatedRecord, isSystemAdmin, openModalOnReissueClick, openModalOnRefundClick } = this.props;

    // PlaceHub data comes from the related record API call
    const fundraiser = get(relatedRecord, 'data.data.fundraiser');
    const eventAmbassadorCharge = get(relatedRecord, 'data.data.eventAmbassadorCharge');

    const exchangeRate = get(financials, "exchangeRate");
    const amount = displayOrgCurrency && exchangeRate
      ? donationAmount * exchangeRate
      : donationAmount;
    const currency = displayOrgCurrency ? get(organization, "currency") : donationCurrency;

    const amountString = currencyHelper.formatCurrency(amount, currency);
    const refunded = get(financials, "refunded");
    const originalCurrencyRefundedAmount = get(financials, "refundedAmount");
    const refundedAmount = displayOrgCurrency && exchangeRate && originalCurrencyRefundedAmount
      ? originalCurrencyRefundedAmount * exchangeRate
      : originalCurrencyRefundedAmount;
    const isPartialRefund = type == "online" && refunded && refundedAmount > 0 && refundedAmount < amount;
    const typeOfDonation = type || "N/A";
    const organizationCurrency = get(organization, "currency");

    const donationPrimaryStatusBackgroundColor =
      financials &&
      donationSettings[typeOfDonation].getPrimaryStatusBackgroundColor(
        refunded, isPartialRefund
      );
    const donationSecondaryStatusBackgroundColor =
      financials &&
      donationSettings[typeOfDonation].getSecondaryStatusBackgroundColor();

    const donationPrimaryStatusValue =
      financials &&
      donationSettings[typeOfDonation].getPrimaryStatusValue(refunded, isPartialRefund);
    const donationSecondaryStatusValue =
      financials && donationSettings[typeOfDonation].getSecondaryStatusValue();

    const emptyFieldsLabel = isShowEmptyFields
      ? I18n.t(`${donationI18Keys.donationHeaderI18Key}.hide-empty-fields-link`)
      : I18n.t(`${donationI18Keys.donationHeaderI18Key}.show-empty-fields-link`);

    const showCurrencyToggle =
      donationCurrency !== organizationCurrency && Boolean(get(financials, "exchangeRate"));

    const currencyToggleLabel = showCurrencyToggle
      ? I18n.t(
          `${donationI18Keys.donationHeaderI18Key}.show-different-currency-link`,
          {
            currency: displayOrgCurrency ? donationCurrency : organizationCurrency
          }
        )
      : '';

    const editButtonText = I18n.t(
      `${donationI18Keys.donationHeaderI18Key}.show-edit-button`
    );
    const actionsTriggerLabel = I18n.t('campaign.performance.header.actions');

    // Donation action helpers
    const organizationUrl = get(organization, 'urlFull');
    const isRefunded = get(financials, 'refunded');
    const isOnline = type === donationType.Online;
    const isOffline = type === donationType.Offline;
    const isPending = type === donationType.Pending;
    const donorId = get(donor, 'donorId');
    const stripeUrl = this.getStripeUrl();

    const handleClickViewReissueReceipt = () => {
      openModalOnReissueClick({ id: donationId, ...donor });
    };

    const handleClickRefundOrDelete = () => {
      openModalOnRefundClick(this.props.donation);
    };

    const getViewReceiptLink = () => {
      return `${organizationUrl}/receipt/${receiptIdentifier}`;
    };

    // Edit action - shown as primary button
    const editAction = this.props.donation ? {
      key: 'edit',
      label: editButtonText,
      icon: <Edit3 aria-hidden="true" />,
      linkTo: `/donations/edit/${this.props.donation.id}/donation-detail`
    } : null;

    // Build action menu items in specified order
    const actionMenuItems = [
      // View Receipt (external link)
      receiptIdentifier && {
        key: CONTEXT_MENU_KEYS.DONATIONS.VIEW_RECEIPT.key,
        label: I18n.t(`${donationI18Keys.donationActionI18}.viewReceipt.title`),
        icon: <Eye aria-hidden="true" />,
        href: getViewReceiptLink(),
        target: '_blank'
      },
      // Reissue Receipt
      {
        key: CONTEXT_MENU_KEYS.DONATIONS.REISSUE_RECEIPT.key,
        label: I18n.t(`${donationI18Keys.donationActionI18}.reissueReceipt.title`),
        icon: <Send aria-hidden="true" />,
        onSelect: handleClickViewReissueReceipt,
        disabled: isPending
      },
      // Refund (online donations only, not refunded)
      isOnline && !isRefunded && {
        key: CONTEXT_MENU_KEYS.DONATIONS.REFUND_DONATION.key,
        label: I18n.t(`${donationI18Keys.donationActionI18}.refund.title`),
        icon: <RotateCcw aria-hidden="true" />,
        onSelect: handleClickRefundOrDelete
      },
      // Delete (offline donations only, not refunded)
      isOffline && !isRefunded && {
        key: CONTEXT_MENU_KEYS.DONATIONS.DELETE_DONATION.key,
        label: I18n.t(`${donationI18Keys.donationActionI18}.delete.title`),
        icon: <Trash2 aria-hidden="true" />,
        onSelect: handleClickRefundOrDelete
      },
      // Separator 1
      { type: 'separator', key: 'separator-1' },
      // View in Stripe (external link)
      stripeUrl && {
        key: CONTEXT_MENU_KEYS.DONATIONS.VIEW_IN_STRIPE.key,
        label: I18n.t(`${donationI18Keys.donationActionI18}.viewInStripe.title`),
        icon: <ExternalLink aria-hidden="true" />,
        href: stripeUrl,
        target: '_blank'
      },
      // View All Donor Donations
      donorId && {
        key: CONTEXT_MENU_KEYS.DONATIONS.VIEW_ALL_DONOR_DONATIONS.key,
        label: I18n.t(`${donationI18Keys.donationActionI18}.viewAllDonorDonation.title`),
        icon: <Eye aria-hidden="true" />,
        linkTo: CONTEXT_MENU_KEYS.DONATIONS.VIEW_ALL_DONOR_DONATIONS.getUrl({ donorId })
      },
      // View Linked Sales Details
      saleId && {
        key: CONTEXT_MENU_KEYS.DONATIONS.VIEW_LINKED_SALE.key,
        label: I18n.t(`${donationI18Keys.donationActionI18}.viewLinkedSalesDetails.title`),
        icon: <Eye aria-hidden="true" />,
        linkTo: CONTEXT_MENU_KEYS.DONATIONS.VIEW_LINKED_SALE.getUrl({ saleId })
      },
      // View Linked Recurring Giving
      subscriptionId && {
        key: CONTEXT_MENU_KEYS.DONATIONS.VIEW_LINKED_DONATION_SUBSCRIPTION.key,
        label: I18n.t(`${donationI18Keys.donationActionI18}.viewLinkedRecurringGiving.title`),
        icon: <Eye aria-hidden="true" />,
        linkTo: CONTEXT_MENU_KEYS.DONATIONS.VIEW_LINKED_DONATION_SUBSCRIPTION.getUrl({ subscriptionId })
      },
      // Separator 2
      { type: 'separator', key: 'separator-2' },
      // Toggle empty fields
      {
        key: 'toggle-empty-fields',
        label: emptyFieldsLabel,
        icon: isShowEmptyFields ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />,
        onSelect: this.props.changeVisibilityEmptyFields
      },
      // Toggle currency display
      showCurrencyToggle && {
        key: 'toggle-currency-display',
        label: currencyToggleLabel,
        icon: <DollarSign aria-hidden="true" />,
        onSelect: this.props.onToggleCurrencyDisplay
      },
      // Audit trail link for system admins
      isSystemAdmin && donationId && {
        key: CONTEXT_MENU_KEYS.DONATIONS.AUDIT_TRAIL.key,
        label: I18n.t('campaign.performance.header.audit-trail'),
        icon: <History aria-hidden="true" />,
        linkTo: CONTEXT_MENU_KEYS.DONATIONS.AUDIT_TRAIL.getUrl({ id: donationId }),
        systemOnly: true
      }
    ];

    return (
      <div className="donation-header">
        <div>{I18n.t(`${donationI18Keys.donationHeaderI18Key}.donation`)}</div>
        <div className="donation-header-content">
          <div className="first-section-block" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <h1>
              <CurrencyDisplay 
                amount={amountString}
                currency={currency}
                showCurrency={true}
              />
            </h1>
            <StatusBadgeGroup
              className="status-type"
              style={{ marginTop: '8px', marginLeft: 0 }}
              badges={[
                donationPrimaryStatusValue && {
                  label: donationPrimaryStatusValue,
                  colorVariant: 'custom',
                  backgroundColor: donationPrimaryStatusBackgroundColor,
                  testId: 'donation-primary-status'
                },
                donationSecondaryStatusValue && {
                  label: donationSecondaryStatusValue,
                  colorVariant: 'custom',
                  backgroundColor: donationSecondaryStatusBackgroundColor,
                  testId: 'donation-secondary-status'
                },
                // PlaceHub badges - only shown for donations (not sales)
                get(fundraiser, 'isAmbassador') && {
                  label: I18n.t(`${donationI18Keys.donationHeaderI18Key}.placehub-badge.accepted`),
                  colorVariant: 'accent',
                  testId: 'placehub-accepted'
                },
                !get(fundraiser, 'isAmbassador') && get(fundraiser, 'isAmbassadorApplicant') && {
                  label: I18n.t(`${donationI18Keys.donationHeaderI18Key}.placehub-badge.applicant`),
                  colorVariant: 'accent',
                  variant: 'outlined',
                  testId: 'placehub-applicant'
                },
                get(eventAmbassadorCharge, 'eventAmbassadorId') && {
                  label: I18n.t(`${donationI18Keys.donationHeaderI18Key}.placehub-badge.fundraising-guarantee`),
                  colorVariant: 'custom',
                  backgroundColor: '#E8891D',
                  variant: 'outlined',
                  testId: 'placehub-fundraising-guarantee'
                }
              ]}
            />
          </div>
          <div className="second-section-block">
            <div style={{ marginLeft: 'auto' }}>
              <EntityHeaderActions
                editAction={editAction}
                menuItems={actionMenuItems}
                actionsLabel={actionsTriggerLabel}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(DonationHeader);
